import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay9() {
  const { lesson, course, accent } = findLesson("sing-and-play", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >

      <AnimatedExplainer
        src="/animations/sing-and-play/SP9-6-8-Perfect-Animated.mp4"
        caption={`6/8 time — two strong pulses, three little notes each`}
        hint="≈ 22 sec · slow-dance heartbeat"
      />
      {/* ── 1. Big idea ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="Lesson 9 · 6/8 Compound Time" title="Perfect — Two Big Beats, Each Divided by Three">
        <p>
          Ed Sheeran released "Perfect" in 2017 and it went to number one across much of
          the world — though the streaming record that year belonged to its own album-mate,
          "Shape of You". Its genius is partly in the melody and lyric — but
          also in the <strong>feel</strong>: that rolling, swaying quality that makes you
          want to move side to side. That quality comes from the time signature: 6/8.
        </p>
        <p>
          6/8 is not "six little beats." It is <strong>two big beats, each divided
          into three equal parts</strong>. Count it as: <em>"ONE-two-three, TWO-two-three"</em> —
          like a waltz that has been stretched wide, or water rocking in a bowl. Once you
          feel the two-beat pulse underneath the six subdivisions, this time signature
          becomes natural.
        </p>
        <SAPCallout label="6/8 vs 3/4 — the common confusion" variant="insight">
          3/4 has three beats, each undivided. You sway <em>left–centre–right</em>.
          6/8 has two beats, each divided by three. You sway <em>left–right</em> but each
          sway takes three steps. The feeling is rounder, more rolling. "Perfect" is
          unmistakably 6/8 — count "ONE-two-three, TWO-two-three" and you'll feel it
          immediately.
        </SAPCallout>
      </SAPSection>

      {/* ── 2. Reference video ──────────────────────────────────────────── */}
      <YouTubeEmbed
        videoId="2Vv-BfVoq4g"
        title="Reference · Ed Sheeran — Perfect (Official)"
        caption="Ed Sheeran — Perfect. The recording is in A♭ major. We'll practise in G major (one semitone lower) before transposing. Listen to the piano in the intro — that's the 6/8 arpeggio we're learning."
      />

      {/* ── 3. The progression ──────────────────────────────────────────── */}
      <SAPSection eyebrow="The Harmony" title="I–vi–IV–V: The Ballad Progression">
        <p>
          The progression is I–vi–IV–V — the most beloved chord sequence in Western pop.
          In G major: <strong>G–Em–C–D</strong>. In A♭ major (the recording):
          <strong> A♭–Fm–D♭–E♭</strong>. Same roman numerals, same emotional arc,
          different pitch level.
        </p>
        <p>
          What makes this progression sing in 6/8 is that each chord gets one full bar
          of six eighth notes. The three-subdivision feel makes the chord <em>arrive
          gracefully</em> rather than snapping in abruptly — each chord has time to
          breathe before the next one arrives.
        </p>

        <RomanNumeralAnalysis
          label="Verse — G major (practice key)"
          caption="G–Em is the gentlest movement in the key — they share two notes (B and D). Em–C drops a third and opens up the harmony. C–D is the pre-dominant to dominant motion that creates the forward lean into the next bar."
          chords={[
            { chord: "G",  rn: "I",  function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "Em", rn: "vi", function: "Submediant",      pitches: ["E3","G3","B3","E4"] },
            { chord: "C",  rn: "IV", function: "Pre-dominant", pitches: ["C3","E3","G3","C4"] },
            { chord: "D",  rn: "V",  function: "Dominant",     pitches: ["D3","F#3","A3","D4"] },
          ]}
        />

        <RomanNumeralAnalysis
          label="Concert pitch — A♭ major (recording key)"
          caption="If you want to play along with Ed Sheeran's recording, use these chords. The voice-leading is identical — just shifted up a semitone."
          chords={[
            { chord: "A♭",  rn: "I",  function: "Tonic",        pitches: ["Ab3","C4","Eb4","Ab4"] },
            { chord: "Fm",  rn: "vi", function: "Submediant",      pitches: ["F3","Ab3","C4","F4"]   },
            { chord: "D♭",  rn: "IV", function: "Pre-dominant", pitches: ["Db3","F3","Ab3","Db4"] },
            { chord: "E♭",  rn: "V",  function: "Dominant",     pitches: ["Eb3","G3","Bb3","Eb4"] },
          ]}
        />

        <SAPChordRow
          label="G major practice chords — click to hear"
          chords={[
            { label: "G",  rn: "I",  pitches: ["G3","B3","D4","G4"],  fn: "tonic"       },
            { label: "Em", rn: "vi", pitches: ["E3","G3","B3","E4"],  fn: "submediant"     },
            { label: "C",  rn: "IV", pitches: ["C3","E3","G3","C4"],  fn: "subdominant" },
            { label: "D",  rn: "V",  pitches: ["D3","F#3","A3","D4"], fn: "dominant"    },
          ]}
        />
      </SAPSection>

      {/* ── 4. The 6/8 LH pattern ───────────────────────────────────────── */}
      <SAPSection eyebrow="Technique · Left Hand" title="The Rolling Bass: Root on 1, Fifth on 4">
        <p>
          In 6/8, the LH plays a <strong>two-point bass</strong>: the root note on beat 1
          (the first of the six eighth notes) and the fifth of the chord on beat 4 (the
          fourth eighth note). The remaining four eighth notes are silence or a gentle
          sustain.
        </p>
        <p>
          Counted across six eighth notes: <strong>Root–·–·–Fifth–·–·</strong>. The
          distance between those two anchor points creates the rolling sensation — like a
          boat rocking on two swells. The silence between them is not empty: it's the
          wave travelling.
        </p>

        <SAPCallout label="Root vs octave bass" variant="tip">
          Beginners: play just the root (single note). Once comfortable, upgrade to
          <strong> root + octave</strong> (e.g. a low G with the G an octave above) for a fuller sound. The octave
          bass adds resonance without changing the pattern. Advanced option: add the fifth
          of the chord as a third note in the LH on beat 4 (low G on beat 1, the D above it on beat 4 —
          the open fifth). That open-fifth LH pattern is the heart of the perfect ballad
          sound.
        </SAPCallout>


      </SAPSection>

      {/* ── 5. The RH arpeggio ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Technique · Right Hand" title="The Arpeggio: Three Notes per Beat Group">
        <p>
          The classic 6/8 arpeggio divides each beat group of three into a rising or
          falling pattern through the chord tones. For G major: play <strong>G on 1,
          B on 2, D on 3</strong> (first beat group), then <strong>B on 4, D on 5,
          the high G on 6</strong> (second beat group). Six notes, six eighth slots, perfectly
          filling the bar.
        </p>
        <p>
          The key is <strong>legato touch</strong> — let each note ring into the next,
          pedal down, so the arpeggio blurs into a shimmer rather than individual notes.
          The magic of "Perfect" is precisely in that shimmer.
        </p>

        <SAPCallout label="The space is the music" variant="insight">
          Ed Sheeran's piano arrangement is notable for what it <em>doesn't</em> play.
          There are whole beats where the arpeggio stops and the LH bass simply rings.
          Don't fill every gap. Let the silence breathe. The most powerful note in any
          ballad is the note you choose not to play.
        </SAPCallout>

        <SAPQuote>
          The piano is an amazing instrument to play 6 by 8 music. However, it's a lot
          more mechanically feasible, you could say, on finger-style guitars.
        </SAPQuote>
      </SAPSection>

      {/* ── 6. OomPah interactive player ─────────────────────────────────── */}
      <SAPSection eyebrow="Interactive · Play Along" title="The 6/8 Loop in G Major">
        <p>
          This player shows the 6/8 pattern in Oom-Pah format — LH bass on beats 1 and 4,
          RH chord on the off-beats. Press play at 75 bpm and feel the rolling sway of
          compound duple time.
        </p>
        <OomPahPlayer
          progression={[
            { label: "G",  bars: 2, lhRoot: "G2",  lhFifth: "D3",  rh: ["G4","B4","D5"] },
            { label: "Em", bars: 2, lhRoot: "E3",  lhFifth: "B3",  rh: ["E4","G4","B4"] },
            { label: "C",  bars: 2, lhRoot: "C3",  lhFifth: "G3",  rh: ["C4","E4","G4"] },
            { label: "D",  bars: 2, lhRoot: "D3",  lhFifth: "A3",  rh: ["D4","F#4","A4"] },
          ]}
          bpm={75}
          label="PERFECT — G major 6/8 loop"
        />
        <SAPCallout label="Feel the two big beats" variant="tip">
          As the loop plays, tap your foot twice per bar — on the 1st and 4th eighth
          notes. Those are your two big beats. Everything else is subdivision. Once your
          foot is locking in on those two points, the 6/8 feel will become natural.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Ghost notes & transposition ──────────────────────────────── */}
      <SAPSection eyebrow="Advanced Colour" title="Ghost-Note Flow & Transposing Keys">
        <p>
          Once the basic arpeggio is comfortable, add <strong>ghost-note fills</strong>:
          soft, passing notes between the main chord tones that ornament the arpeggio
          without changing its underlying structure. On G major: instead of G–B–D,
          try G–A–B–D — the A is a passing note, barely voiced, more felt than heard.
          This is the technique that makes a piano arpeggio sound like it's breathing.
        </p>
        <p>
          Transposition is the highest goal. Play the I–vi–IV–V loop in G, then in C,
          then in F, then attempt A♭ (the recording key). The chord shapes change; the
          Roman numerals — and the feeling — stay exactly the same.
        </p>

        <SAPChordRow
          label="In C major — same feel, new key"
          chords={[
            { label: "C",  rn: "I",  pitches: ["C3","E3","G3","C4"],  fn: "tonic"       },
            { label: "Am", rn: "vi", pitches: ["A3","C4","E4","A4"],  fn: "submediant"     },
            { label: "F",  rn: "IV", pitches: ["F3","A3","C4","F4"],  fn: "subdominant" },
            { label: "G",  rn: "V",  pitches: ["G3","B3","D4","G4"],  fn: "dominant"    },
          ]}
          caption="C major is often the best key to try second — all white keys makes the hand positions easier to see. Build the 6/8 feel here before tackling sharps and flats."
        />
      </SAPSection>

      {/* ── 8. Practice ladder ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Your Practice Plan" title="30-Minute Perfect Workout">
        <SAPPracticeLadder
          intro="6/8 is a feel that has to be lived, not just understood. Work through these rungs slowly — the rolling quality you're after comes from relaxation, not precision."
          rungs={[
            {
              title: "Tap and count — no keys",
              minutes: 3,
              detail: "Tap your left hand on the piano lid twice per bar: \"ONE — TWO —\" while counting \"ONE-two-three, TWO-two-three\" aloud. Feel the big beats before you play.",
              accent: "amber",
            },
            {
              title: "LH root only",
              minutes: 5,
              detail: "G on beat 1 of every bar. Hold it, let it ring. Change chord at bar 2 (Em), bar 3 (C), bar 4 (D). Just one note per bar. Feel the harmonic rhythm.",
              accent: "purple",
            },
            {
              title: "LH root + fifth",
              minutes: 5,
              detail: "Add the fifth on beat 4: low G on beat 1, the D above it on beat 4. Two anchor points per bar. The boat starts to rock.",
              accent: "cyan",
            },
            {
              title: "RH three-note arpeggio",
              minutes: 7,
              detail: "Add G–B–D in the RH across beats 1–2–3, then D–B–G descending on beats 4–5–6. Pedal down. Let it shimmer.",
              accent: "emerald",
            },
            {
              title: "Hands together — full progression",
              minutes: 5,
              detail: "G–Em–C–D at 70 bpm. Feel both hands locking into the rolling sway. Don't rush the tempo.",
              accent: "rose",
            },
            {
              title: "Sing \"I found a love\"",
              minutes: 5,
              detail: "Hum the verse melody while playing the loop. The melody sits on top of the arpeggio like cream on coffee — don't force it, let it float.",
              accent: "amber",
            },
          ]}
        />
        <SAPQuote>
          Remember, this has to suit your voice. That's very important. Now, the
          original song is on A♭ because it works for Ed Sheeran's voice, for the
          emotion, the vibe and the texture which is bringing out his voice the best.
        </SAPQuote>
      </SAPSection>


      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/09-perfect.png"
          caption="Jason's chord chart — Perfect by Ed Sheeran in A♭ major"
        />
      </SAPSection>
    </LessonShell>
  );
}
